/**
 * Decide whether THIS build may send errors to the production Sentry projects.
 *
 * THE DEFECT THIS CLOSES (OFFICEHUB-CLIENT-65). Every client entry initialised
 * Sentry on one condition — "is a DSN present". `client/.env` holds the
 * PRODUCTION DSN and CRA loads it for `npm start` exactly as for
 * `npm run build`, so every local run and every Playwright run reported into
 * the production project. Measured over the 7 days to 2026-09-21: 52 of 98
 * client error events carried `environment: development` — 53%, a majority of
 * the signal — from `localhost:3003`, HeadlessChrome and `*@e2e.test` users.
 * One issue (CLIENT-65) reached `substatus: escalating` on entirely fake
 * traffic. The `environment` tag was correct the whole time; nothing acted on
 * it.
 *
 * TWO LAYERS LIVE HERE, and the third is not code:
 *   1. `sentryInitDecision` — do not initialise at all off a real deployment.
 *      This alone removes the 53%.
 *   2. `createProductionOnlyBeforeSend` — belt and braces for any build that
 *      slips past layer 1, and it COUNTS what it drops so the suppression can
 *      never be mistaken for silence.
 *   3. A Sentry-side inbound filter on `environment:development` is the only
 *      layer that protects against a build nobody rebuilt. It is a console
 *      action, not a code change, and is NOT done by this file.
 *
 * A THIRD DUTY, added 2026-10-06: TAG, never drop, traffic from our own automated browsers.
 * Layers 1 and 2 key on the build's environment and host, so a headless UI check pointed at the
 * PRODUCTION hostname passes both — measured that day: all 4 new client issues were HeadlessChrome
 * 143 on Linux (a test account's 403, a deliberate example.com fetch, Google Maps' WebGL warning)
 * and each re-opened the board. Such an event is still sent, now carrying `automation: "true"`, so
 * boards and alerts can filter it (`!automation:true`). Dropping it was ruled out: a UI check that
 * finds a REAL defect must still be able to report it.
 *
 * WHY THE HOST CHECK EXISTS AS WELL AS THE MODE CHECK. `npm run build` sets
 * NODE_ENV=production, so a locally-served production bundle — exactly what an
 * E2E run against a built app looks like — passes the mode check. The host is
 * what distinguishes it.
 *
 * WHY NO NEW OPT-IN ENV VAR. Requiring a variable CI must set would mean a
 * missing variable silently disables production error reporting — trading a
 * noise problem for a blindness problem. Mode + host are both already true of
 * every real deployment and of no local run.
 *
 * SHARED-MODULE RULES THIS OBEYS (see buildIdentity.ts / clientEntriesWired):
 * it imports NOTHING — no `@sentry/*` bare specifier that a consumer app might
 * lack, and no app-specific singleton. Every input is passed in by the caller,
 * because `process.env.NODE_ENV` (CRA) and `import.meta.env.MODE` (Vite) are
 * different expressions and only the app's own entry can name its own.
 */

export interface SentryGateInput {
  /** The configured DSN, or undefined when none is set. */
  readonly dsn: string | undefined | null;
  /** `process.env.NODE_ENV` (CRA) or `import.meta.env.MODE` (Vite). */
  readonly mode: string | undefined;
  /** `window.location.hostname`, or undefined where there is no browser. */
  readonly hostname: string | undefined;
}

export type SentryGateDecision =
  | { readonly enabled: true }
  | { readonly enabled: false; readonly reason: string };

/**
 * Hosts that can only be this machine. `''`/undefined is included on purpose:
 * an unknown host is not evidence of a deployment, and this gate fails closed.
 */
function isLocalHost(hostname: string | undefined): boolean {
  if (!hostname) return true;
  const host = hostname.trim().toLowerCase().replace(/^\[|\]$/g, '');
  if (host === '') return true;
  if (host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0' || host === '::1') {
    return true;
  }
  // `.local` is mDNS (a machine on the LAN); `.localhost` is reserved by RFC 6761.
  return host.endsWith('.local') || host.endsWith('.localhost');
}

/**
 * Whether to initialise Sentry, and — when not — a stable machine-readable
 * reason. The reason is what makes "reporting is off" diagnosable instead of
 * mysterious.
 */
export function sentryInitDecision(input: SentryGateInput): SentryGateDecision {
  const dsn = typeof input.dsn === 'string' ? input.dsn.trim() : '';
  if (!dsn) return { enabled: false, reason: 'no-dsn' };
  if (input.mode !== 'production') {
    return { enabled: false, reason: `non-production-build:${input.mode ?? 'unknown'}` };
  }
  if (isLocalHost(input.hostname)) return { enabled: false, reason: 'local-host' };
  return { enabled: true };
}

/** `sentryInitDecision(...).enabled`, for the `if` in an entry file. */
export function shouldInitSentry(input: SentryGateInput): boolean {
  return sentryInitDecision(input).enabled;
}

/** Minimal shape this module needs from a Sentry event. */
interface EnvironmentBearingEvent {
  readonly environment?: string;
  readonly tags?: Readonly<Record<string, unknown>>;
}

/** The Sentry tag set on events from our own automated browsers. Filter with `!automation:true`. */
export const AUTOMATION_TAG = 'automation';

/** The two `navigator` fields automation detection reads (kept structural: no DOM lib needed). */
interface NavigatorLike {
  readonly webdriver?: boolean;
  readonly userAgent?: string;
}

/**
 * Is this browser driven by automation (Playwright / Puppeteer / Selenium, headed or headless)?
 * `navigator.webdriver` is set by the WebDriver and CDP automation stacks, and a headless Chrome
 * also names itself in its user agent. Either is enough. A person's browser sets neither.
 *
 * Returns false (never throws) when there is no navigator, e.g. a non-browser runtime.
 */
export function isAutomatedBrowser(nav: NavigatorLike | undefined): boolean {
  if (!nav) return false;
  if (nav.webdriver === true) return true;
  return /HeadlessChrome/i.test(nav.userAgent ?? '');
}

/** Test seam: how to decide an event came from an automated browser. Defaults to the real navigator. */
export interface BeforeSendOptions {
  readonly isAutomated?: () => boolean;
}

let droppedCount = 0;

/** How many non-production events layer 2 has refused to send this session. */
export function nonProductionEventsDropped(): number {
  return droppedCount;
}

/**
 * A `beforeSend` that refuses anything not stamped `production`.
 *
 * An event with NO environment is dropped too: unknown provenance is not
 * evidence of production, and the same fail-closed reading applies here as in
 * the host check.
 *
 * The drop is COUNTED and announced once, because a filter that cannot be
 * distinguished from "nothing happened" is how a suppression outlives its
 * reason.
 *
 * A PRODUCTION event is never dropped here, but one from an automated browser
 * (`isAutomatedBrowser`: WebDriver flag or a HeadlessChrome user agent) is
 * returned as a NEW copy tagged `automation: "true"` so boards and alerts can
 * filter it (`!automation:true`). The input is never mutated; every other
 * production event is returned as the same object, untouched.
 *
 * @param options Test seam: `isAutomated` overrides the real navigator check.
 */
export function createProductionOnlyBeforeSend(
  options: BeforeSendOptions = {},
): <T extends EnvironmentBearingEvent>(event: T) => T | null {
  const isAutomated =
    options.isAutomated ??
    (() => isAutomatedBrowser(typeof navigator === 'undefined' ? undefined : navigator));
  return <T extends EnvironmentBearingEvent>(event: T): T | null => {
    if (event?.environment === 'production') {
      // A real user's event is returned as the SAME object. Only an automated browser's is
      // copied (never mutated) with the tag added; it is still sent, never dropped.
      return isAutomated() ? ({ ...event, tags: { ...event.tags, [AUTOMATION_TAG]: 'true' } } as T) : event;
    }
    droppedCount += 1;
    if (droppedCount === 1) {
      // console.warn, never console.error: the app's console interceptor
      // reports console.error to the very projects this gate is protecting.
      // eslint-disable-next-line no-console
      console.warn(
        `[sentry-gate] dropping events not stamped environment=production (first: ${
          event?.environment ?? 'none'
        }). Further drops are counted, not logged.`,
      );
    }
    return null;
  };
}
